import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AudioPlayer from '../components/AudioPlayer';
import MobileMediaActionBar from '../components/MobileMediaActionBar';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import SkipToContent from '../components/SkipToContent';
import AdminErrorBoundary from '../components/admin/AdminErrorBoundary';
import { RECORD_ROUTE_PATTERNS } from '../lib/recordRoutes';
import { publicAdSurfaceAllowed } from '../lib/adDelivery';
import { MetaPixelTracker, ScrollToTop } from './RouteEffects';
import { ADMIN_ROUTE_PATTERNS, isAdminPath } from './routeContracts';
import useSiteBootstrap from './useSiteBootstrap';

const HomePageKenya = lazy(() => import('../pages/HomePageKenya'));
const Wave2Homepage = lazy(() => import('../pages/Wave2Homepage'));
const RastyleVerseGatewayPage = lazy(() => import('../pages/RastyleVerseGatewayPage'));
const VibePage = lazy(() => import('../pages/VibePage'));
const UniversalContentPage = lazy(() => import('../pages/UniversalContentPage'));
const ShopCollectionPage = lazy(() => import('../pages/ShopCollectionPage'));
const ShortLinkRedirect = lazy(() => import('../pages/ShortLinkRedirect'));
const LegacyRedirectHandler = lazy(() => import('../pages/LegacyRedirectHandler'));
const GamingRoomPage = lazy(() => import('../pages/GamingRoomPage'));
const ClubModePage = lazy(() => import('../pages/ClubModePage'));
const StaffConsolePage = lazy(() => import('../pages/StaffConsolePage'));
const HeatmapPage = lazy(() => import('../pages/HeatmapPage'));
const OddsRoomPage = lazy(() => import('../pages/OddsRoomPage'));
const CreatorLeaderboardPage = lazy(() => import('../pages/CreatorLeaderboardPage'));
const FanPublicProfilePage = lazy(() => import('../pages/FanPublicProfilePage'));
const AdminOddsPage = lazy(() => import('../pages/AdminOddsPage'));
const AfrikaShopPage = lazy(() => import('../pages/AfrikaShopPage'));
const AfrikaProductDetailPage = lazy(() => import('../pages/AfrikaProductDetailPage'));
const AdminAfrikaShopPage = lazy(() => import('../pages/AdminAfrikaShopPage'));
const MixtapesPage = lazy(() => import('../pages/MixtapesPage'));
const MixtapeDetailPage = lazy(() => import('../pages/MixtapeDetailPage'));
const PlaylistsPage = lazy(() => import('../pages/PlaylistsPage'));
const PlaylistDetailPage = lazy(() => import('../pages/PlaylistDetailPage'));
const SharedPlaylistPage = lazy(() => import('../pages/SharedPlaylistPage'));
const RecordsPage = lazy(() => import('../pages/RecordsPage'));
const VideoMixesPage = lazy(() => import('../pages/VideoMixesPage'));
const VideoDetailPage = lazy(() => import('../pages/VideoDetailPage'));
const ReleaseDetailPage = lazy(() => import('../pages/ReleaseDetailPage'));
const LegacyRecordRedirect = lazy(() => import('../pages/LegacyRecordRedirect'));
const ShopPage = lazy(() => import('../pages/ShopPage'));
const ProductDetailPage = lazy(() => import('../pages/ProductDetailPage'));
const EventsPage = lazy(() => import('../pages/EventsPage'));
const EventDetailPage = lazy(() => import('../pages/EventDetailPage'));
const VenuePage = lazy(() => import('../pages/VenuePage'));
const RadioPage = lazy(() => import('../pages/RadioPage'));
const CommunityPage = lazy(() => import('../pages/CommunityPage'));
const AboutPage = lazy(() => import('../pages/AboutPage'));
const JoinPage = lazy(() => import('../pages/JoinPage'));
const AdminPage = lazy(() => import('../pages/AdminPage'));
const LegacyCreatorRedirect = lazy(() => import('../pages/LegacyCreatorRedirect'));
const ContactPage = lazy(() => import('../pages/ContactPage'));
const NewsPage = lazy(() => import('../pages/NewsPage'));
const NewsDetailPage = lazy(() => import('../pages/NewsDetailPage'));
const EditorProfilePage = lazy(() => import('../pages/EditorProfilePage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('../pages/AuthCallbackPage'));
const SocialLinkPage = lazy(() => import('../pages/SocialLinkPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const AccountWelcomePage = lazy(() => import('../pages/AccountWelcomePage'));
const MembershipPage = lazy(() => import('../pages/MembershipPage'));
const MembershipSuccessPage = lazy(() => import('../pages/MembershipSuccessPage'));
const MyTicketsPage = lazy(() => import('../pages/MyTicketsPage'));
const TicketSuccessPage = lazy(() => import('../pages/TicketSuccessPage'));
const TicketVerifyPage = lazy(() => import('../pages/TicketVerifyPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const CreatorsPage = lazy(() => import('../pages/CreatorsPage'));
const CreatorProfilePage = lazy(() => import('../pages/CreatorProfilePage'));
const ArtistsDirectoryPage = lazy(() => import('../pages/ArtistsDirectoryPage'));
const DeejaysDirectoryPage = lazy(() => import('../pages/DeejaysDirectoryPage'));
const CreatorDashboardPage = lazy(() => import('../pages/CreatorDashboardPage'));
const CreatorLandingPage = lazy(() => import('../pages/CreatorLandingPage'));
const CreatorWelcomePage = lazy(() => import('../pages/CreatorWelcomePage'));
const CreatorProfileSettingsPage = lazy(() => import('../pages/CreatorProfileSettingsPage'));
const CreateEventPage = lazy(() => import('../pages/CreateEventPage'));
const RasFestPage = lazy(() => import('../pages/RasFestPage'));
const DailyChallengePage = lazy(() => import('../pages/DailyChallengePage'));
const ServiceProviderPage = lazy(() => import('../pages/ServiceProviderPage'));
const CreatorSettingsPage = lazy(() => import('../pages/CreatorSettingsPage'));
const TriviaEmbedPage = lazy(() => import('../pages/TriviaEmbedPage'));
const ComingSoonPage = lazy(() => import('../pages/ComingSoonPage'));
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('../pages/TermsOfServicePage'));
const ReturnsPolicyPage = lazy(() => import('../pages/ReturnsPolicyPage'));
const DataDeletionPage = lazy(() => import('../pages/DataDeletionPage'));
const PromotePage = lazy(() => import('../pages/PromotePage'));
const RewardsPage = lazy(() => import('../pages/RewardsPage'));
const SocialStudioPage = lazy(() => import('../pages/SocialStudioPage'));
const GameModePage = lazy(() => import('../pages/GameModePage'));
const VenueTVPage = lazy(() => import('../pages/VenueTVPage'));
const AktivatePage = lazy(() => import('../pages/AktivatePage'));
const AktivateDirectoryPage = lazy(() => import('../pages/AktivateDirectoryPage'));
const BecomeCreatorPage = lazy(() => import('../pages/BecomeCreatorPage'));
const LegalPage = lazy(() => import('../pages/LegalPage'));
const ReggaeGatePage = lazy(() => import('../pages/ReggaeGatePage'));
const ReggaeAtlasPage = lazy(() => import('../pages/ReggaeAtlasPage'));
const ReggaeRadarPage = lazy(() => import('../pages/ReggaeRadarPage'));
const MissionsPage = lazy(() => import('../pages/MissionsPage'));
const LeaderboardPage = lazy(() => import('../pages/LeaderboardPage'));
const FollowingPage = lazy(() => import('../pages/FollowingPage'));
const TimelinePage = lazy(() => import('../pages/TimelinePage'));
const WhatsAppInboxPage = lazy(() => import('../pages/WhatsAppInboxPage'));
const UnifiedAdSlot = lazy(() => import('../components/GoogleAd'));

function RouteFallback({ fullScreen = false }) {
  return (
    <div className={`${fullScreen ? 'min-h-screen' : 'min-h-[40vh]'} flex items-center justify-center`} role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-primary" />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );
}

function AdminRoutes() {
  return (
    <AdminErrorBoundary>
      <Suspense fallback={<RouteFallback fullScreen />}>
        <Routes>
          <Route path={ADMIN_ROUTE_PATTERNS.odds} element={<AdminOddsPage />} />
          <Route path={ADMIN_ROUTE_PATTERNS.afrikaShop} element={<AdminAfrikaShopPage />} />
          <Route path={ADMIN_ROUTE_PATTERNS.root} element={<AdminPage />} />
        </Routes>
      </Suspense>
    </AdminErrorBoundary>
  );
}

function PublicRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Wave2Homepage />} />
        <Route path="/home-legacy-preview" element={<HomePageKenya />} />
        <Route path="/explore" element={<RastyleVerseGatewayPage />} />
        <Route path="/content/:entityId" element={<UniversalContentPage />} />
        <Route path="/vibes/:slug" element={<VibePage />} />
        <Route path="/afrika-shop/collections/:slug" element={<ShopCollectionPage />} />
        <Route path="/s/:code" element={<ShortLinkRedirect />} />
        <Route path="/gaming" element={<GamingRoomPage />} />
        <Route path="/games" element={<GamingRoomPage />} />
        <Route path="/club/:venueId" element={<ClubModePage />} />
        <Route path="/staff/whatsapp-inbox" element={<WhatsAppInboxPage />} />
        <Route path="/staff/:venueId" element={<StaffConsolePage />} />
        <Route path="/heatmap" element={<HeatmapPage />} />
        <Route path="/odds" element={<OddsRoomPage />} />
        <Route path="/creator-leaderboard" element={<CreatorLeaderboardPage />} />
        <Route path="/fan/:userId" element={<FanPublicProfilePage />} />
        <Route path="/afrika-shop" element={<AfrikaShopPage />} />
        <Route path="/afrika-shop/:productId" element={<AfrikaProductDetailPage />} />
        <Route path="/home-global" element={<Navigate to="/" replace />} />
        <Route path="/home-old" element={<Navigate to="/" replace />} />
        <Route path="/mixtapes" element={<MixtapesPage />} />
        <Route path="/mixtapes/:mixtapeId" element={<MixtapeDetailPage />} />
        <Route path="/playlists" element={<PlaylistsPage />} />
        <Route path="/playlists/shared/:shareCode" element={<SharedPlaylistPage />} />
        <Route path="/playlists/:playlistId" element={<PlaylistDetailPage />} />
        <Route path={RECORD_ROUTE_PATTERNS.index} element={<RecordsPage />} />
        <Route path={RECORD_ROUTE_PATTERNS.video} element={<VideoDetailPage />} />
        <Route path={RECORD_ROUTE_PATTERNS.release} element={<ReleaseDetailPage />} />
        <Route path={RECORD_ROUTE_PATTERNS.legacy} element={<LegacyRecordRedirect />} />
        <Route path="/video-mixes" element={<VideoMixesPage />} />
        <Route path="/video-mixes/:videoId" element={<VideoDetailPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/:handle" element={<ProductDetailPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:eventId" element={<EventDetailPage />} />
        <Route path="/venues/:venueId" element={<VenuePage />} />
        <Route path="/my-tickets" element={<MyTicketsPage />} />
        <Route path="/ticket-success" element={<TicketSuccessPage />} />
        <Route path="/verify-ticket" element={<TicketVerifyPage />} />
        <Route path="/radio" element={<RadioPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/media" element={<Navigate to="/video-mixes" replace />} />
        <Route path="/dj/:djId" element={<LegacyCreatorRedirect />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:articleId" element={<NewsDetailPage />} />
        <Route path="/editors/:slug" element={<EditorProfilePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/social/link" element={<SocialLinkPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/welcome" element={<AccountWelcomePage />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/membership/success" element={<MembershipSuccessPage />} />
        <Route path="/coming-soon-preview" element={<ComingSoonPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/creators" element={<CreatorsPage />} />
        <Route path="/artists" element={<ArtistsDirectoryPage />} />
        <Route path="/artists/:slug" element={<LegacyCreatorRedirect />} />
        <Route path="/deejays" element={<DeejaysDirectoryPage />} />
        <Route path="/deejays/:slug" element={<LegacyCreatorRedirect />} />
        <Route path="/become-creator" element={<CreatorLandingPage />} />
        <Route path="/creator-onboard" element={<BecomeCreatorPage />} />
        <Route path="/creators/apply" element={<Navigate to="/creator-onboard" replace />} />
        <Route path="/creator/welcome" element={<CreatorWelcomePage />} />
        <Route path="/creator/dashboard" element={<CreatorDashboardPage />} />
        <Route path="/creator/settings/profile" element={<CreatorProfileSettingsPage />} />
        <Route path="/creator/events/new" element={<CreateEventPage />} />
        <Route path="/creator/:slug" element={<CreatorProfilePage />} />
        <Route path="/ras-fest" element={<RasFestPage />} />
        <Route path="/trivia" element={<Navigate to="/gaming" replace />} />
        <Route path="/trivia/:gameId" element={<Navigate to="/gaming" replace />} />
        <Route path="/daily-challenge" element={<DailyChallengePage />} />
        <Route path="/provider-dashboard" element={<ServiceProviderPage />} />
        <Route path="/creator/settings" element={<CreatorSettingsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/returns-policy" element={<ReturnsPolicyPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        <Route path="/promote" element={<PromotePage />} />
        <Route path="/rewards" element={<RewardsPage />} />
        <Route path="/social-studio" element={<SocialStudioPage />} />
        <Route path="/aktivate" element={<AktivatePage />} />
        <Route path="/aktivate/:category" element={<AktivateDirectoryPage />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/reggae-gate" element={<ReggaeGatePage />} />
        <Route path="/atlas" element={<ReggaeAtlasPage />} />
        <Route path="/radar" element={<ReggaeRadarPage />} />
        <Route path="/missions" element={<MissionsPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/following" element={<FollowingPage />} />
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="*" element={<LegacyRedirectHandler />} />
      </Routes>
    </Suspense>
  );
}

export default function AppRouter() {
  const location = useLocation();
  const { user, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const siteStatus = useSiteBootstrap();

  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<RouteFallback fullScreen />}>
        <AuthCallbackPage />
      </Suspense>
    );
  }

  if (location.pathname.startsWith('/trivia/embed')) {
    return (
      <Suspense fallback={<RouteFallback fullScreen />}>
        <Routes>
          <Route path="/trivia/embed/:embedId" element={<TriviaEmbedPage />} />
          <Route path="/trivia/embed" element={<TriviaEmbedPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.startsWith('/game/')) {
    return (
      <Suspense fallback={<RouteFallback fullScreen />}>
        <Routes>
          <Route path="/game/:roomId" element={<GameModePage />} />
          <Route path="/game/:roomId/venue" element={<VenueTVPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname === '/coming-soon-preview') {
    return (
      <Suspense fallback={<RouteFallback fullScreen />}>
        <ComingSoonPage />
      </Suspense>
    );
  }

  const adminPath = isAdminPath(location.pathname);
  const isAdminUser = isAuthenticated && isAdmin;
  const justLoggedOut = typeof window !== 'undefined' && sessionStorage.getItem('admin_logged_out');
  const hasAdminToken = typeof window !== 'undefined' && !justLoggedOut && localStorage.getItem('admin_token');

  if (adminPath) {
    if (authLoading) return <RouteFallback fullScreen />;
    if (isAuthenticated && !isAdminUser) {
      return <Navigate to={user?.is_creator ? '/creator/dashboard' : '/'} replace />;
    }
    return (
      <>
        <ScrollToTop />
        <MetaPixelTracker />
        <AdminRoutes />
      </>
    );
  }

  if (siteStatus.checked && siteStatus.coming_soon && !adminPath && !authLoading && !isAdminUser && !hasAdminToken) {
    return (
      <Suspense fallback={<RouteFallback fullScreen />}>
        <ComingSoonPage />
      </Suspense>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SkipToContent />
      <Navigation />
      <main id="main-content" tabIndex="-1" className="site-public-main pt-16 outline-none">
        <PublicRoutes />
      </main>
      {publicAdSurfaceAllowed(location.pathname) && (
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6" data-testid="sitewide-ad-slot">
          <Suspense fallback={null}>
            <UnifiedAdSlot placement="footer" variant="banner" title="Sponsored" />
          </Suspense>
        </div>
      )}
      <Footer />
      <AudioPlayer />
      <MobileMediaActionBar />
      <PWAInstallPrompt />
    </>
  );
}
